import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Textarea from "../../components/common/Textarea";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import {
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
} from "../../store/api";

const RoleForm = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(roleId);

  const { data: roleData } = useGetRoleQuery(roleId, { skip: !isEditing });
  const [createRole, createState] = useCreateRoleMutation();
  const [updateRole, updateState] = useUpdateRoleMutation();

  const [form, setForm] = useState({
    name: "",
    description: "",
    businessId: "",
    isGlobal: true,
  });

  useEffect(() => {
    if (isEditing && roleData) {
      setForm({
        name: roleData.name || roleData.roleName || "",
        description: roleData.description || "",
        businessId: roleData.businessId || "",
        isGlobal: !roleData.businessId,
      });
    }
  }, [isEditing, roleData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "isGlobal") {
      setForm((prev) => ({
        ...prev,
        isGlobal: checked,
        businessId: checked ? "" : prev.businessId,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        description: form.description,
        businessId: form.isGlobal ? null : form.businessId || null,
      };

      if (isEditing) {
        await updateRole({ roleId, payload }).unwrap();
      } else {
        await createRole(payload).unwrap();
      }
      navigate("/roles");
    } catch (err) {
      // error handled by UI
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? "Edit Role" : "Create Role"}
        subtitle={
          isEditing ? "Update role details." : "Add a new role to the system."
        }
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate("/roles")}
          >
            Back
          </Button>
        }
      />

      <Card>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Role Name"
              name="name"
              placeholder="Business Manager"
              value={form.name}
              onChange={handleChange}
              required
            />
            <Checkbox
              label="Global Role"
              name="isGlobal"
              checked={form.isGlobal}
              onChange={handleChange}
              helper="Available across all businesses"
            />
          </div>

          <Textarea
            label="Description"
            name="description"
            placeholder="Manages business operations..."
            value={form.description}
            onChange={handleChange}
          />

          {!form.isGlobal && (
            <Input
              label="Business ID"
              name="businessId"
              placeholder="Enter business ID"
              value={form.businessId}
              onChange={handleChange}
              helper="Leave empty for global role"
            />
          )}

          {(createState.isSuccess || updateState.isSuccess) && (
            <Alert tone="success">
              Role {isEditing ? "updated" : "created"} successfully.
            </Alert>
          )}
          {(createState.isError || updateState.isError) && (
            <Alert tone="error">
              {createState.error?.data?.detail ||
                updateState.error?.data?.detail ||
                "Failed to save role."}
            </Alert>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={createState.isLoading || updateState.isLoading}
            >
              {isEditing ? "Update" : "Create"} Role
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/roles")}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default RoleForm;
